import { Button } from '@components/common';
import { CheckCircle2, Circle, ExternalLink, LucideIcon } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

interface FirstTimeSetupStepCardProps {
  actionLabel: string;
  description: string;
  guideLabel?: string;
  icon: LucideIcon;
  isCompleted: boolean;
  isOptional?: boolean;
  isSaving?: boolean;
  route: string;
  stepNumber: number;
  title: string;
  onStartGuide?: () => void;
  onToggleComplete: () => void;
}

export const FirstTimeSetupStepCard: React.FC<FirstTimeSetupStepCardProps> = ({
  actionLabel,
  description,
  guideLabel = 'Guide Me',
  icon: Icon,
  isCompleted,
  isOptional = false,
  isSaving = false,
  onStartGuide,
  route,
  stepNumber,
  title,
  onToggleComplete,
}) => (
  <div
    className={`rounded-2xl border p-5 shadow-sm transition-colors ${
      isCompleted
        ? 'border-emerald-200 bg-emerald-50/70'
        : 'border-gray-200 bg-white'
    }`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3">
        <div
          className={`flex h-11 w-11 items-center justify-center rounded-2xl ${
            isCompleted
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-pos-primary/10 text-pos-primary'
          }`}
        >
          <Icon size={20} />
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500">
              Step {stepNumber}
            </span>
            <span
              className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${
                isOptional
                  ? 'bg-gray-100 text-gray-500'
                  : 'bg-emerald-100 text-emerald-700'
              }`}
            >
              {isOptional ? 'Optional' : 'Recommended'}
            </span>
          </div>
          <h3 className="mt-3 text-lg font-semibold text-gray-900">{title}</h3>
          <p className="mt-2 text-sm leading-6 text-gray-600">{description}</p>
        </div>
      </div>

      <div className="pt-1">
        {isCompleted ? (
          <CheckCircle2 size={22} className="text-emerald-600" />
        ) : (
          <Circle size={22} className="text-gray-300" />
        )}
      </div>
    </div>

    <div className="mt-5 flex flex-wrap gap-3">
      {onStartGuide ? (
        <Button type="button" onClick={onStartGuide}>
          {guideLabel}
        </Button>
      ) : null}

      <Link
        to={route}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
      >
        <span>{actionLabel}</span>
        <ExternalLink size={15} />
      </Link>

      <Button
        type="button"
        variant={isCompleted ? 'outline' : 'secondary'}
        isLoading={isSaving}
        onClick={onToggleComplete}
      >
        {isCompleted ? 'Mark as not done' : 'Mark as done'}
      </Button>
    </div>
  </div>
);
